import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';

import { CampaignAction, CampaignCreationSource, CampaignStatus } from '../types';

export const getIsCampaignEditable = (options: {
  campaignType: CampaignType;
  isCreatedByWolt?: boolean;
  creationSource?: CampaignCreationSource;
  campaignStatus?: CampaignStatus;
  country?: Country;
  isMerchantLevel: boolean;
  venueIds: string[];
}) => {
  const { campaignType, campaignStatus, isCreatedByWolt, country, creationSource, isMerchantLevel, venueIds } = options;

  if (!isMerchantLevel && venueIds.length > 1) {
    return false;
  }

  if (isCreatedByWolt && campaignType !== CampaignType.ADS) {
    return false;
  }

  if (creationSource === 'opsTools' && campaignType === CampaignType.ADS) {
    return false;
  }

  if (campaignType === CampaignType.END_OF_DAY) {
    return false;
  }

  if (campaignStatus && campaignStatus === CampaignStatus.SCHEDULED) {
    return true;
  }

  if (campaignStatus && campaignStatus === CampaignStatus.RUNNING) {
    if (country === 'ISR' && campaignType !== CampaignType.ADS) {
      return false;
    }
    return true;
  }

  if (campaignStatus && campaignStatus === CampaignStatus.STOPPED && campaignType === CampaignType.ADS) {
    return true;
  }

  return false;
};

export const getIsCampaignEndable = (options: {
  campaignType: CampaignType;
  campaignStatus?: CampaignStatus;
  isCreatedByWolt?: boolean;
  country?: Country;
  isMerchantLevel: boolean;
  venueIds: string[];
}) => {
  const { campaignType, campaignStatus, isCreatedByWolt, country, isMerchantLevel, venueIds } = options;

  if (!isMerchantLevel && venueIds.length > 1) {
    return false;
  }

  if (campaignType === CampaignType.ADS || isCreatedByWolt || country === 'ISR') {
    return false;
  }
  return campaignStatus === CampaignStatus.RUNNING;
};

export const getIsCampaignReadable = (options: {
  campaignType: CampaignType;
  creationSource?: CampaignCreationSource;
  isCreatedByWolt?: boolean;
}) => {
  const { campaignType, isCreatedByWolt, creationSource } = options;

  if (isCreatedByWolt && campaignType === CampaignType.ITEM_DISCOUNT) {
    return false;
  }

  if (campaignType === CampaignType.ADS) {
    if (creationSource === 'opsTools') {
      return false;
    }
    return true;
  }

  return true;
};

export const getIsCampaignDeletable = (options: {
  campaignType: CampaignType;
  campaignStatus?: CampaignStatus;
  isCreatedByWolt?: boolean;
  isMerchantLevel: boolean;
  venueIds: string[];
}) => {
  const { campaignType, isCreatedByWolt, campaignStatus, isMerchantLevel, venueIds } = options;
  if (!isMerchantLevel && venueIds.length > 1) {
    return false;
  }
  if (isCreatedByWolt || campaignType === CampaignType.ADS) {
    return false;
  }
  return campaignStatus === CampaignStatus.SCHEDULED;
};

export const getIsCampaignStoppable = (options: {
  campaignType: CampaignType;
  campaignStatus?: CampaignStatus;
  creationSource?: CampaignCreationSource;
  isMerchantLevel: boolean;
  venueIds: string[];
}) => {
  const { campaignType, creationSource, campaignStatus, isMerchantLevel, venueIds } = options;
  if (!isMerchantLevel && venueIds.length > 1) {
    return false;
  }
  if (campaignType !== CampaignType.ADS || creationSource === 'opsTools') {
    return false;
  }

  return campaignStatus !== CampaignStatus.STOPPED && campaignStatus !== CampaignStatus.COMPLETED;
};

export const getIsCampaignRestartable = (options: {
  campaignType: CampaignType;
  campaignStatus?: CampaignStatus;
  creationSource?: CampaignCreationSource;
}) => {
  const { campaignType, creationSource, campaignStatus } = options;
  if (campaignType !== CampaignType.ADS || creationSource === 'opsTools') {
    return false;
  }

  return campaignStatus === CampaignStatus.STOPPED;
};

export const getIsCampaignCloneable = (options: {
  campaignType: CampaignType;
  isCreatedByWolt?: boolean;
  isMerchantLevel: boolean;
  venueIds: string[];
}) => {
  const { campaignType, isCreatedByWolt, isMerchantLevel, venueIds } = options;
  if (!isMerchantLevel && venueIds.length > 1) {
    return false;
  }
  return campaignType !== CampaignType.ADS && !isCreatedByWolt;
};

export type GetCampaignActionsValuesType = {
  campaignType: CampaignType;
  campaignStatus: CampaignStatus;
  country: Country;
  isCreatedByWolt?: boolean;
  creationSource?: CampaignCreationSource;
  isMerchantLevel: boolean;
  venueIds: string[];
};

export const getCampaignActions = (values: GetCampaignActionsValuesType): CampaignAction[] => {
  const { campaignType, campaignStatus, country, isCreatedByWolt, creationSource, isMerchantLevel, venueIds } = values;

  const isCampaignEditable = getIsCampaignEditable({
    campaignType,
    isCreatedByWolt,
    campaignStatus,
    country,
    creationSource,
    isMerchantLevel,
    venueIds,
  });
  const isCampaignReadable = getIsCampaignReadable({
    isCreatedByWolt,
    campaignType,
    creationSource,
  });
  const isCampaignEndable = getIsCampaignEndable({
    campaignType,
    campaignStatus,
    country,
    isCreatedByWolt,
    isMerchantLevel,
    venueIds,
  });
  const isCampaignDeletable = getIsCampaignDeletable({
    isCreatedByWolt,
    campaignType,
    campaignStatus,
    isMerchantLevel,
    venueIds,
  });
  const isCampaignCloneable = getIsCampaignCloneable({ campaignType, isCreatedByWolt, isMerchantLevel, venueIds });
  const isCampaignStoppable = getIsCampaignStoppable({
    campaignType,
    campaignStatus,
    creationSource,
    isMerchantLevel,
    venueIds,
  });
  const isCampaignRestartable = getIsCampaignRestartable({ campaignType, campaignStatus, creationSource });
  return [
    ...(isCampaignCloneable ? ['clone' as CampaignAction] : []),
    ...(isCampaignDeletable ? ['delete' as CampaignAction] : []),
    ...(isCampaignEditable ? ['edit' as CampaignAction] : []),
    ...(isCampaignReadable ? ['read' as CampaignAction] : []),
    ...(isCampaignRestartable ? ['restart' as CampaignAction] : []),
    ...(isCampaignStoppable ? ['stop' as CampaignAction] : []),
    ...(isCampaignEndable ? ['terminate' as CampaignAction] : []),
  ];
};
